import { Helmet } from "react-helmet-async";
import { useTenant } from "@/hooks/useTenant";
import { useSiteAsset } from "@/hooks/useSiteAsset";
import { useShareImage } from "@/hooks/useShareImage";
import { useCopyResolver } from "@/hooks/useSiteCopy";
import { resolveShareText, socialDescriptionKey, socialTitleKey, type SocialPageId } from "@/lib/siteCopy";

interface SeoProps {
  title: string;
  description: string;
  /** Path beginning with "/", used for canonical and og:url. */
  path: string;
  /** The page's own share image (absolute, or site-relative). */
  image?: string;
  imageAlt?: string;
  /** Stated only when the image is known to be exactly this size. */
  imageWidth?: number;
  imageHeight?: number;
  imageType?: string;
  /** Which public page this is, so the admin's per-page share card and share
   *  title / description (Site assets → Social sharing) apply. */
  social?: SocialPageId;
  /** og:type, defaults to "website". Use "article" for posts, "product" for listings. */
  ogType?: string;
  /** One JSON-LD object or an array of them. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** When true, search engines won't index but may follow links. */
  noindex?: boolean;
  /** ISO timestamp for article:modified_time. */
  modifiedTime?: string;
}

const Seo = ({
  title,
  description,
  path,
  image,
  imageAlt,
  imageWidth,
  imageHeight,
  imageType,
  social,
  ogType = "website",
  jsonLd,
  noindex,
  modifiedTime,
}: SeoProps) => {
  const tenant = useTenant();
  // Per-city uploads and copy (Admin → Site assets). All resolve from cached
  // queries, so this costs no extra request.
  const favicon = useSiteAsset("brand.favicon");
  const share = useShareImage({
    social,
    explicit: image,
    explicitAlt: imageAlt,
    explicitDims: imageWidth && imageHeight ? { width: imageWidth, height: imageHeight, type: imageType } : null,
  });
  const copy = useCopyResolver();
  // Share text overrides touch only the social tags; <title> and the meta
  // description stay page-owned.
  const shareTitle = social ? resolveShareText({ override: copy(socialTitleKey(social)).value, fallback: title }) : title;
  const shareDescription = social
    ? resolveShareText({ override: copy(socialDescriptionKey(social)).value, fallback: description })
    : description;
  const url = `${tenant.siteUrl}${path.startsWith("/") ? path : `/${path}`}`;
  const faviconUrl = !favicon.isFallback && favicon.src ? favicon.src : null;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  const robots = noindex
    ? "noindex,follow"
    : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />
      {faviconUrl && <link rel="icon" href={faviconUrl} />}
      {faviconUrl && <link rel="apple-touch-icon" href={faviconUrl} />}

      {/* Open Graph */}
      <meta property="og:title" content={shareTitle} />
      <meta property="og:description" content={shareDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={share.url} />
      {share.width ? <meta property="og:image:width" content={String(share.width)} /> : null}
      {share.height ? <meta property="og:image:height" content={String(share.height)} /> : null}
      {share.type ? <meta property="og:image:type" content={share.type} /> : null}
      <meta property="og:image:alt" content={share.alt} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={tenant.siteName} />
      <meta property="og:locale" content="en_US" />
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={shareTitle} />
      <meta name="twitter:description" content={shareDescription} />
      <meta name="twitter:image" content={share.url} />
      <meta name="twitter:image:alt" content={share.alt} />

      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(b)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
